// 导入axios实例
import httpRequest from "@/apis/axios-api";

import { GetJudgeTypeStr, GetSpaceTypeStr } from "@/apis/judge.ts";

import type { Problem, ProblemDaily, ProblemDailyView, ProblemTag, ProblemView } from "@/types/problem";
import type { QueryParams } from "@/types/request.ts";
import { UploadImageTokenResponse } from "@/util/md-editor-v3.ts";
import { ProblemRank, ProblemRankView } from "@/types/rank.ts";
import { formatTime, formatMemory } from "@/util/parse.ts";
import { JudgeLanguage } from "@/apis/language.ts";
import { AiInterruptedError, AiRequestError, streamAiNdjson } from "@/apis/ai.ts";
import type { AiStreamCallbacks } from "@/apis/ai.ts";

export enum ProblemAttemptStatus {
  None = 0,
  Attempt = 1,
  Accept = 2,
}

export enum ProblemRankType {
  Time = 0,
  Memory = 1,
  CodeLength = 2,
}

export function GetProblemRankTypeStr(type: ProblemRankType) {
  switch (type) {
    case ProblemRankType.Time:
      return "优先用时";
    case ProblemRankType.Memory:
      return "优先内存";
    case ProblemRankType.CodeLength:
      return "优先代码长度";
    default:
      return "";
  }
}

export function GetProblemRankTypes() {
  return Object.values(ProblemRankType)
    .filter((v) => typeof v === "number")
    .map((value) => ({
      value,
      label: GetProblemRankTypeStr(value as ProblemRankType),
    })) as { label: string; value: ProblemRankType }[];
}

export function ParseProblem(item: Problem, tagsMap: { [key: number]: ProblemTag }): ProblemView {
  const result: ProblemView = {} as ProblemView;
  result.id = item.id;
  result.key = item.key;
  result.inserter = item.inserter;
  result.title = item.title;
  result.tags = [];
  if (item.tags) {
    for (const tag of item.tags) {
      const tagData = tagsMap[tag];
      if (tagData) {
        result.tags.push(tagData);
      }
    }
  }
  result.private = item.private;
  if (item.accept) {
    result.accept = item.accept;
  } else {
    result.accept = 0;
  }
  if (item.attempt) {
    result.attempt = item.attempt;
  } else {
    result.attempt = 0;
  }

  if (item.description != undefined) {
    result.description = item.description;
  }
  if (item.time_limit != undefined && item.time_limit >= 0) {
    result.timeLimit = item.time_limit.toString() + " ms";
  } else {
    result.timeLimit = "未知";
  }
  if (item.memory_limit != undefined && item.memory_limit >= 0) {
    result.memoryLimit = (item.memory_limit / 1024).toFixed(2) + " MB";
  } else {
    result.memoryLimit = "未知";
  }
  if (item.judge_type != undefined) {
    result.judgeType = GetJudgeTypeStr(item.judge_type);
  }
  if (item.space_type != undefined) {
    result.spaceType = GetSpaceTypeStr(item.space_type);
  }
  if (item.insert_time != undefined) {
    result.insertTime = new Date(item.insert_time).toLocaleString();
  }
  if (item.modify_time != undefined) {
    result.modifyTime = new Date(item.modify_time).toLocaleString();
  }
  if (item.modifier_nickname != undefined) {
    result.inserterNickname = item.modifier_nickname;
  }
  if (item.source != undefined) {
    // 判断item.source是否符合[]()格式，是否赋值result.sourceUrl
    const urlRegex = /\[([^\]]+)\]\(([^)]+)\)/;
    const match = item.source.match(urlRegex);
    if (match) {
      result.source = match[1];
      result.sourceUrl = match[2];
    } else {
      result.source = item.source;
      result.sourceUrl = "";
    }
  }
  if (item.judge_md5 != undefined) {
    result.judgeMd5 = item.judge_md5;
  }
  if (item.origin_oj != undefined) {
    result.originOj = item.origin_oj;
  }
  if (item.origin_id != undefined) {
    result.originId = item.origin_id;
  }
  if (item.origin_url != undefined) {
    result.originUrl = item.origin_url;
  }
  if (item.origin_author != undefined) {
    result.originAuthor = item.origin_author;
  }
  if (item.template != undefined) {
    result.template = item.template;
  }
  if (item.immutable_regions != undefined) {
    result.immutableRegions = item.immutable_regions;
  }
  if (item.difficulty != undefined) {
    result.difficulty = item.difficulty;
  }
  if (item.difficulty_name != undefined) {
    result.difficultyName = item.difficulty_name;
  }

  return result;
}

export function ParseProblemDaily(item: ProblemDaily, tagsMap: { [key: number]: ProblemTag }): ProblemDailyView {
  const result: ProblemDailyView = {} as ProblemDailyView;
  result.key = item.key;
  result.problemId = item.problem_id;
  result.problemKey = item.problem_key;
  result.title = item.title;
  result.tags = [];
  if (item.tags) {
    for (const tag of item.tags) {
      const tagData = tagsMap[tag];
      if (tagData) {
        result.tags.push(tagData);
      }
    }
  }
  if (item.accept) {
    result.accept = item.accept;
  } else {
    result.accept = 0;
  }
  if (item.attempt) {
    result.attempt = item.attempt;
  } else {
    result.attempt = 0;
  }
  result.solution = item.solution;
  result.code = item.code;

  if (item.insert_time != undefined) {
    result.insertTime = new Date(item.insert_time).toLocaleString();
  }
  if (item.modify_time != undefined) {
    result.modifyTime = new Date(item.modify_time).toLocaleString();
  }
  if (item.inserter_nickname != undefined) {
    result.inserterNickname = item.inserter_nickname;
  }
  if (item.modifier_nickname != undefined) {
    result.modifierNickname = item.modifier_nickname;
  }

  return result;
}

export function ParseProblemRank(item: ProblemRank) {
  const result: ProblemRankView = {} as ProblemRankView;

  result.id = item.id;
  if (item.time) {
    result.time = formatTime(item.time);
  } else {
    result.time = "-";
  }
  if (item.memory) {
    result.memory = formatMemory(item.memory);
  } else {
    result.memory = "-";
  }

  result.language = item.language;
  result.codeLength = item.code_length;
  if (item.insert_time) {
    result.insertTime = new Date(item.insert_time).toLocaleString();
  } else {
    result.insertTime = "-";
  }
  result.inserter = item.inserter;
  result.inserterUsername = item.inserter_username;
  result.inserterNickname = item.inserter_nickname;
  result.inserterEmail = item.inserter_email;
  result.private = item.private;
  return result;
}

export function GetProblem(problemKey: string, contestId: number | undefined, problemIndex: number | undefined) {
  const params: QueryParams = {};
  if (problemKey) {
    params.key = problemKey;
  } else {
    params.contest_id = String(contestId);
    params.problem_index = String(problemIndex);
  }
  return httpRequest({
    url: `/problem?${new URLSearchParams(params).toString()}`,
    method: "get",
  });
}

export function GetProblemTagList(num: number) {
  return httpRequest({
    url: "/problem/tag/list" + "?max_count=" + num,
    method: "get",
  });
}

export function GetProblemJudge(problemId: string) {
  return httpRequest({
    url: "/problem/judge" + "?key=" + problemId,
    method: "get",
  });
}

export function GetProblemList(
  oj: string,
  title: string,
  tag: string,
  privateData: boolean,
  page: number,
  pageSize: number,
  difficulty = 0,
  tagMode = "any"
) {
  const params: QueryParams = {};
  if (oj) {
    params.oj = oj;
  }
  if (title) {
    params.title = title;
  }
  if (tag) {
    params.tag = tag;
    // 多个标签之间的关系：any 命中任一 / all 每组都要具备。
    // 只在真有标签条件时传，避免给后端塞一个无意义的参数
    if (tagMode === "all") {
      params.tag_mode = "all";
    }
  }
  if (difficulty >= 1 && difficulty <= 5) {
    params.difficulty = String(difficulty);
  }
  if (privateData !== undefined) {
    params.private = privateData ? "1" : "0";
  }
  if (page) {
    params.page = String(page);
  }
  if (pageSize) {
    params.page_size = String(pageSize);
  }

  return httpRequest({
    url: `/problem/list?${new URLSearchParams(params).toString()}`,
    method: "get",
  });
}

export function GetProblemRecommend(problemKey: string) {
  const params: QueryParams = {};
  if (problemKey) {
    params.problem_key = problemKey;
  }
  return httpRequest({
    url: `/problem/recommend?${new URLSearchParams(params).toString()}`,
    method: "get",
  });
}

export function GetProblemDaily(dailyId: string) {
  return httpRequest({
    url: "/problem/daily" + "?key=" + dailyId,
    method: "get",
  });
}

export function GetProblemDailyEdit(dailyId: string) {
  return httpRequest({
    url: "/problem/daily/edit" + "?key=" + dailyId,
    method: "get",
  });
}

export function GetProblemDailyRecently() {
  return httpRequest({
    url: "/problem/daily/recently",
    method: "get",
  });
}

export function GetProblemDailyList(startDate: string, endData: string, problemKey: string, page: number, pageSize: number) {
  const params: QueryParams = {};
  if (startDate) {
    params.start_date = startDate;
  }
  if (endData) {
    params.end_date = endData;
  }
  if (problemKey) {
    params.problem_key = problemKey;
  }
  if (page) {
    params.page = String(page);
  }
  if (pageSize) {
    params.page_size = String(pageSize);
  }

  return httpRequest({
    url: `/problem/daily/list?${new URLSearchParams(params).toString()}`,
    method: "get",
  });
}

export function GetProblemStatistics(problemKey: string, language: JudgeLanguage | undefined) {
  return httpRequest({
    url: "/problem/statistics",
    method: "get",
    params: {
      key: problemKey,
      language: language,
    },
  });
}

export function GetProblemRank(problemKey: string, language: JudgeLanguage | undefined, rankType: ProblemRankType) {
  return httpRequest({
    url: "/problem/rank",
    method: "get",
    params: {
      key: problemKey,
      language: language,
      type: rankType,
    },
  });
}

export function GetProblemAttemptStatus(problemIds: number[]) {
  return httpRequest({
    url: "/problem/attempt/status",
    method: "get",
    params: {
      ids: problemIds.join(","),
    },
  });
}

export function GetProblemAttemptStatusByKey(problemKeys: string[]) {
  return httpRequest({
    url: "/problem/attempt/status/key",
    method: "get",
    params: {
      keys: problemKeys.join(","),
    },
  });
}

// 获取下一题
export function GetNextProblem(problemId: number) {
  return httpRequest({
    url: "/problem/next",
    method: "get",
    params: {
      id: problemId,
    },
  });
}

// 获取上一题
export function GetPrevProblem(problemId: number) {
  return httpRequest({
    url: "/problem/prev",
    method: "get",
    params: {
      id: problemId,
    },
  });
}

export function PostProblemCrawl(oj: string, problemKey: string) {
  return httpRequest({
    url: "/problem/crawl",
    method: "post",
    data: {
      oj: oj,
      key: problemKey,
    },
  });
}

export function PostProblemCreate(
  title: string,
  timeLimit: number,
  memoryLimit: number,
  source: string,
  privateProblem: boolean,
  tags: string[],
  description: string,
  spaceType: number
) {
  return httpRequest({
    url: "/problem/create",
    method: "post",
    data: {
      title: title,
      time_limit: timeLimit,
      memory_limit: memoryLimit,
      source: source,
      private: privateProblem,
      tags: tags,
      description: description,
      space_type: spaceType,
    },
  });
}

export function PostProblemEdit(
  problemId: number,
  title: string,
  timeLimit: number,
  memoryLimit: number,
  source: string,
  privateProblem: boolean,
  tags: string[],
  description: string,
  spaceType: number
) {
  return httpRequest({
    url: "/problem/edit",
    method: "post",
    data: {
      id: problemId,
      title: title,
      time_limit: timeLimit,
      memory_limit: memoryLimit,
      source: source,
      private: privateProblem,
      tags: tags,
      description: description,
      space_type: spaceType,
    },
  });
}

export function PostProblemDailyCreate(id: string, problemKey: string, solution: string, code: string) {
  return httpRequest({
    url: "/problem/daily/create",
    method: "post",
    data: {
      id: id,
      problem_key: problemKey,
      solution: solution,
      code: code,
    },
  });
}

export function PostProblemDailyEdit(dailyId: string, problemKey: string, solution: string, code: string) {
  return httpRequest({
    url: "/problem/daily/edit",
    method: "post",
    data: {
      id: dailyId,
      problem_key: problemKey,
      solution: solution,
      code: code,
    },
  });
}

export function GetJudgeDataDownload(id: number) {
  return httpRequest({
    url: "/problem/judge/data/download" + "?id=" + id,
    method: "get",
  });
}

export function PostJudgeDataBuy(id: number) {
  return httpRequest({
    url: "/problem/judge/data/buy" + "?id=" + id,
    method: "post",
  });
}

export function PostJudgeDataPrice(id: number, price: number) {
  return httpRequest({
    url: "/problem/judge/data/price" + "?id=" + id,
    method: "post",
    data: {
      price: price,
    },
  });
}

export function GetProblemImageToken(problemId: number): Promise<UploadImageTokenResponse> {
  if (!problemId) {
    problemId = 0;
  }
  return httpRequest({
    url: "/problem/image/token" + "?id=" + problemId,
    method: "get",
  });
}

export function GetProblemDailyImageToken(dailyId: string): Promise<UploadImageTokenResponse> {
  if (!dailyId) {
    dailyId = "";
  }
  return httpRequest({
    url: "/problem/daily/image/token" + "?id=" + dailyId,
    method: "get",
  });
}

export function PostJudgeData(id: number, zip: File) {
  const formData = new FormData();
  formData.append("id", String(id));
  formData.append("zip", zip);
  return httpRequest({
    url: "/problem/judge/data",
    method: "post",
    data: formData,
    headers: {
      "Content-Type": "multipart/form-data",
    },
    timeout: 1000 * 60 * 5, // 设置超时时间为5分钟
  });
}

export function PostJudgeDataAi(problemId: number, hint: string) {
  return httpRequest({
    url: "/problem/judge/data/ai",
    method: "post",
    data: {
      problem_id: problemId,
      hint: hint,
    },
  });
}

export function GetJudgeDataAi(id: number) {
  return httpRequest({
    url: "/problem/judge/data/ai?id=" + id,
    method: "get",
  });
}

export function PostJudgeDataAiCancel(id: number) {
  return httpRequest({
    url: "/problem/judge/data/ai/cancel",
    method: "post",
    data: {
      id: id,
    },
  });
}

export function PostJudgeDataAiRetry(id: number, retryKeys: string[] = []) {
  return httpRequest({
    url: "/problem/judge/data/ai/retry",
    method: "post",
    data: {
      id: id,
      retry_keys: retryKeys,
    },
  });
}

export function PostJudgeDataAiApply(id: number, dropKeys: string[]) {
  return httpRequest({
    url: "/problem/judge/data/ai/apply",
    method: "post",
    data: {
      id: id,
      drop_keys: dropKeys,
    },
  });
}

export function GetJudgeDataAiList(problemId: number) {
  return httpRequest({
    url: "/problem/judge/data/ai/list?problem_id=" + problemId,
    method: "get",
  });
}

export function GetAiPreviewZip(jobId: number) {
  return httpRequest({
    url: "/problem/judge/data/ai/zip?job_id=" + jobId,
    method: "get",
  });
}

export function PostProblemParseId(problemIds: number[]) {
  return httpRequest({
    url: "/problem/parse/id",
    method: "post",
    data: {
      problems: problemIds,
    },
  });
}

export function PostProblemParse(problemKeys: string[]) {
  return httpRequest({
    url: "/problem/parse",
    method: "post",
    data: {
      problems: problemKeys,
    },
  });
}

// ── AI 相关：流式能力已提炼到公共客户端（src/apis/ai.ts）──
// 保留原有导出名，避免破坏 AIEnhancement.vue 等既有引用。

export { AiInterruptedError as AIEnhancementInterruptedError, AiRequestError as AIEnhancementRequestError };
export type AIEnhancementCallbacks = AiStreamCallbacks;

// 题面 AI 美化：单轮生成，流式返回美化后的 Markdown
export async function PostProblemAIEnhancement(
  description: string,
  callbacks: AIEnhancementCallbacks,
  signal?: AbortSignal,
) {
  const baseURL = import.meta.env.VITE_API_BASE || "/api/";
  await streamAiNdjson(`${baseURL}problem/ai/enhancement`, { description }, callbacks, signal);
}
